/**
 * SmartLayoutEngine - Intelligent Content-Aware Layout System
 * 
 * This engine analyzes visual components and makes intelligent decisions about
 * text rendering, container relationships, and responsive behavior.
 * 
 * PHASES:
 * - Phase 1: Core infrastructure (COMPLETE)
 * - Phase 2: Analysis and detection (NEXT)
 * - Phase 3: Smart rendering implementation
 */

import React from 'react';
import type {
  SmartElement,
  SmartLayoutConfig,
  RenderPlan,
  ElementDecision,
  LayoutAnalysis,
  Rectangle,
  Dimensions,
  RenderQuality,
  RenderMode
} from './SmartLayout.types';
import { SmartElementDetector } from './SmartElementDetector';
import { SpaceCalculator } from './SpaceCalculator';
import type { SpaceAnalysis, LayoutConflict } from './SpaceCalculator';

export class SmartLayoutEngine {
  /**
   * Phase 1: Core method signatures (NOT IMPLEMENTED YET)
   * These are placeholders for the actual implementation
   */
  
  /**
   * Analyze layout without making changes (Phase 2 - IMPLEMENTED)
   * Safe to call - will not affect rendering
   */
  static analyze(
    elements: SmartElement[],
    containerDimensions: Dimensions,
    globalScale: number = 1
  ): LayoutAnalysis {
    const elementAnalysis = elements.map(element => {
      const spaceAnalysis = SpaceCalculator.analyzeSpace(
        element,
        containerDimensions,
        globalScale,
        elements
      );

      const textMetrics = this.analyzeTextMetrics(element, spaceAnalysis);
      const recommendations = this.generateElementRecommendations(element, spaceAnalysis);

      return {
        element,
        availableSpace: spaceAnalysis.availableSpace,
        textMetrics,
        recommendations
      };
    });

    // Detect layout conflicts
    const conflicts = SpaceCalculator.detectConflicts(elements, globalScale);

    // Overall assessment
    const overallAssessment = this.assessOverallLayout(elementAnalysis, conflicts);

    return {
      containerDimensions,
      globalScale,
      elementAnalysis,
      overallAssessment
    };
  }
  
  /**
   * Generate render plan for intelligent layout (Phase 3 - IMPLEMENTED)
   */
  static generateRenderPlan(
    elements: SmartElement[],
    containerDimensions: Dimensions,
    config: SmartLayoutConfig,
    globalScale: number = 1
  ): RenderPlan {
    // Validate inputs
    if (!elements || elements.length === 0) {
      return {
        elements: [],
        overallQuality: 1,
        hasExternalLabels: false,
        requiresLegend: false,
        recommendations: []
      };
    }

    // Analyze current layout
    const analysis = this.analyze(elements, containerDimensions, globalScale);
    
    // Generate decisions for each element
    const elementDecisions: ElementDecision[] = [];
    let hasExternalLabels = false;
    let totalQuality = 0;

    for (const elementAnalysis of analysis.elementAnalysis) {
      const decision = this.makeRenderDecision(
        elementAnalysis.element,
        elementAnalysis,
        config,
        elements // For conflict-aware decisions
      );
      
      elementDecisions.push(decision);
      totalQuality += decision.quality.overallScore;
      
      if (decision.chosenMode === 'external-label') {
        hasExternalLabels = true;
      }
    }

    // Resolve conflicts between decisions
    const resolvedDecisions = this.resolveConflicts(elementDecisions, analysis);

    // Calculate overall quality
    const overallQuality = elementDecisions.length > 0 ? 
      totalQuality / elementDecisions.length : 1;

    // Generate plan-level recommendations
    const recommendations = this.generatePlanRecommendations(
      resolvedDecisions, 
      analysis, 
      config
    );

    return {
      elements: resolvedDecisions,
      overallQuality,
      hasExternalLabels,
      requiresLegend: hasExternalLabels || resolvedDecisions.some(d => d.chosenMode === 'conceptual-bar'),
      recommendations
    };
  }
  
  /**
   * Validate that the system can handle the given configuration
   * This is safe to call and will not throw
   */
  static validateConfig(config: SmartLayoutConfig): {
    isValid: boolean;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];
    let isValid = true;
    
    // Basic validation
    if (!config) {
      errors.push('SmartLayoutConfig is required');
      isValid = false;
    }
    
    if (config?.enabled && (!config.elements || config.elements.length === 0)) {
      warnings.push('Smart layout enabled but no elements defined');
    }
    
    if (config?.preferences?.minQualityThreshold && 
        (config.preferences.minQualityThreshold < 0 || config.preferences.minQualityThreshold > 1)) {
      errors.push('minQualityThreshold must be between 0 and 1');
      isValid = false;
    }
    
    return { isValid, warnings, errors };
  }
  
  /**
   * Check if smart layout is properly configured and ready
   */
  static isReady(config?: SmartLayoutConfig): boolean {
    if (!config?.enabled) return false;
    
    const validation = this.validateConfig(config);
    return validation.isValid;
  }
  
  /**
   * Get fallback behavior when smart layout fails or is disabled
   */
  static getFallbackMode(config?: SmartLayoutConfig): 'legacy' | 'scale' | 'conceptual' | 'external-labels' {
    return config?.fallbackMode || 'legacy';
  }

  /**
   * Auto-detect elements from React children (Phase 2 - IMPLEMENTED)
   * Scans component children to find text containers automatically
   */
  static autoDetectElements(
    children: React.ReactNode,
    containerBounds: { width: number; height: number },
    config: {
      scanDepth?: number;
      minTextLength?: number;
      confidenceThreshold?: number;
    } = {}
  ): SmartElement[] {
    const bounds = {
      x: 0,
      y: 0,
      width: containerBounds.width,
      height: containerBounds.height
    };

    return SmartElementDetector.detectElements(children, bounds, config);
  }

  /**
   * Private helper methods for analyze()
   */
  private static analyzeTextMetrics(element: SmartElement, spaceAnalysis: SpaceAnalysis) {
    const text = element.content.text || '';
    const maxLines = element.constraints.maxLines;
    
    const optimalFit = SpaceCalculator.calculateOptimalFontSize(
      text,
      spaceAnalysis.availableSpace,
      maxLines
    );

    const estimatedLines = SpaceCalculator.calculateTextLines(
      text,
      spaceAnalysis.availableSpace.width,
      optimalFit.fontSize
    );

    const wouldFit = optimalFit.quality.fits && optimalFit.quality.overallScore > 0.5;

    return {
      estimatedLines,
      minFontSizeRequired: optimalFit.fontSize,
      wouldFit
    };
  }

  private static generateElementRecommendations(
    element: SmartElement,
    spaceAnalysis: SpaceAnalysis
  ): RenderMode[] {
    const recommendations: RenderMode[] = [];
    const quality = spaceAnalysis.quality;
    const text = element.content.text || '';

    // Full text recommendation
    if (quality.readability > 0.7 && quality.spaciousness > 0.6) {
      recommendations.push('full-text');
    }

    // Abbreviated text recommendation
    if (text.length > 20 && quality.readability > 0.5) {
      recommendations.push('abbreviated');
    }

    // External label recommendation
    if (quality.spaciousness < 0.4 && element.content.priority !== 'optional') {
      recommendations.push('external-label');
    }

    // Conceptual representation recommendation
    if (quality.spaciousness < 0.3 && element.content.priority === 'optional') {
      recommendations.push('conceptual-bar');
    }

    // Icon-only for buttons with low space
    if (element.content.contextType === 'button' && quality.spaciousness < 0.5) {
      recommendations.push('icon-only');
    }

    // Hide recommendation for very low priority and space
    if (element.content.priority === 'optional' && quality.spaciousness < 0.2) {
      recommendations.push('hidden');
    }

    // Ensure we have at least one recommendation
    if (recommendations.length === 0) {
      recommendations.push('full-text');
    }

    return recommendations;
  }

  private static assessOverallLayout(
    elementAnalysis: any[],
    conflicts: LayoutConflict[]
  ) {
    let totalQuality = 0;
    const problematicElements: string[] = [];
    const suggestedImprovements: string[] = [];

    // Analyze each element
    elementAnalysis.forEach(analysis => {
      const element = analysis.element;
      const quality = analysis.textMetrics.wouldFit ? 0.8 : 0.3;
      totalQuality += quality;

      if (!analysis.textMetrics.wouldFit) {
        problematicElements.push(element.id);
        
        if (element.content.priority === 'critical') {
          suggestedImprovements.push(`Critical element "${element.id}" may not fit - consider external labeling`);
        }
      }
    });

    // Factor in conflicts
    conflicts.forEach(conflict => {
      totalQuality -= conflict.severity === 'critical' ? 0.3 : 0.1;
      
      if (conflict.severity === 'critical') {
        suggestedImprovements.push(`Critical overlap between ${conflict.elementA} and ${conflict.elementB}`);
      }
    });

    const qualityScore = elementAnalysis.length > 0 ? totalQuality / elementAnalysis.length : 1;

    // Generate improvement suggestions
    if (conflicts.length > 0) {
      suggestedImprovements.push('Consider using external labels or conceptual representation for overlapping elements');
    }

    if (problematicElements.length > elementAnalysis.length / 2) {
      suggestedImprovements.push('Container may be too small for current text content - consider larger dimensions');
    }

    return {
      qualityScore: Math.max(0, Math.min(1, qualityScore)),
      problematicElements,
      suggestedImprovements
    };
  }

  /**
   * Phase 3 helper methods for smart rendering decisions
   */
  private static makeRenderDecision(
    element: SmartElement,
    analysis: any,
    config: SmartLayoutConfig,
    allElements: SmartElement[]
  ): ElementDecision {
    const preferences = config.preferences || {};
    const minQuality = preferences.minQualityThreshold || 0.5;
    
    // Try each render mode in order of preference
    for (const mode of element.renderModes) {
      const quality = this.evaluateRenderMode(element, mode, analysis);
      
      if (quality.overallScore >= minQuality) {
        const renderProps = this.calculateRenderProps(element, mode, analysis);
        
        return {
          element,
          chosenMode: mode,
          quality,
          renderProps,
          fallbackUsed: false
        };
      }
    }

    // No mode met quality threshold - use best available with fallback flag
    const bestMode = this.findBestRenderMode(element, analysis);
    const quality = this.evaluateRenderMode(element, bestMode, analysis);
    const renderProps = this.calculateRenderProps(element, bestMode, analysis);

    return {
      element,
      chosenMode: bestMode,
      quality,
      renderProps,
      fallbackUsed: true
    };
  }

  private static evaluateRenderMode(
    element: SmartElement,
    mode: RenderMode,
    analysis: any
  ): RenderQuality {
    const availableSpace = analysis.availableSpace;
    const text = element.content.text || '';
    
    switch (mode) {
      case 'full-text':
        return SpaceCalculator.testTextFit(
          text,
          availableSpace,
          analysis.textMetrics.minFontSizeRequired,
          element.constraints.maxLines
        );
        
      case 'abbreviated':
        const abbreviated = this.generateAbbreviatedText(text);
        return SpaceCalculator.testTextFit(
          abbreviated,
          availableSpace,
          analysis.textMetrics.minFontSizeRequired,
          element.constraints.maxLines
        );
        
      case 'icon-only':
        return {
          fits: true,
          readabilityScore: 0.6, // Icons are less readable than text
          aestheticScore: 0.8,   // But often look cleaner
          informationRetention: 0.7, // Some info loss
          overallScore: 0.7
        };
        
      case 'conceptual-bar':
        return {
          fits: true,
          readabilityScore: 0.3, // Low readability
          aestheticScore: 0.7,   // Clean appearance
          informationRetention: 0.4, // Significant info loss
          overallScore: 0.5
        };
        
      case 'external-label':
        return {
          fits: true, // External labels always "fit"
          readabilityScore: 0.9, // Highly readable
          aestheticScore: 0.6,   // May look cluttered
          informationRetention: 1.0, // No info loss
          overallScore: 0.8
        };
        
      case 'hidden':
        return {
          fits: true,
          readabilityScore: 0,
          aestheticScore: 0.9, // Clean because invisible
          informationRetention: 0,
          overallScore: 0.3 // Low overall due to no information
        };
        
      default:
        return {
          fits: false,
          readabilityScore: 0,
          aestheticScore: 0,
          informationRetention: 0,
          overallScore: 0
        };
    }
  }

  private static calculateRenderProps(
    element: SmartElement,
    mode: RenderMode,
    analysis: any
  ) {
    const availableSpace = analysis.availableSpace;
    const text = element.content.text || '';
    
    const props: any = {
      fontSize: analysis.textMetrics.minFontSizeRequired,
      position: availableSpace
    };

    switch (mode) {
      case 'full-text':
        props.text = text;
        break;
        
      case 'abbreviated':
        props.text = this.generateAbbreviatedText(text);
        break;
        
      case 'icon-only':
        props.text = ''; // No text
        props.icon = element.content.iconFallback || this.getDefaultIcon(element.content.contextType);
        break;
        
      case 'conceptual-bar':
        props.text = ''; // No text
        props.barWidth = Math.min(availableSpace.width * 0.8, text.length * 8);
        props.barHeight = Math.min(availableSpace.height * 0.3, 12);
        break;
        
      case 'external-label':
        props.text = text;
        props.externalLabelPosition = this.calculateExternalLabelPosition(element, availableSpace);
        break;
        
      case 'hidden':
        props.text = '';
        props.visible = false;
        break;
    }

    return props;
  }

  private static findBestRenderMode(element: SmartElement, analysis: any): RenderMode {
    let bestMode: RenderMode = 'full-text';
    let bestScore = 0;

    for (const mode of element.renderModes) {
      const quality = this.evaluateRenderMode(element, mode, analysis);
      if (quality.overallScore > bestScore) {
        bestScore = quality.overallScore;
        bestMode = mode;
      }
    }

    return bestMode;
  }

  private static resolveConflicts(
    decisions: ElementDecision[],
    analysis: any
  ): ElementDecision[] {
    // For Phase 3, implement basic conflict resolution
    // More sophisticated resolution can be added later
    
    const conflicts = SpaceCalculator.detectConflicts(
      decisions.map(d => d.element),
      analysis.globalScale
    );

    // Resolve critical conflicts by changing render modes
    conflicts.forEach(conflict => {
      if (conflict.severity === 'critical') {
        const elementADecision = decisions.find(d => d.element.id === conflict.elementA);
        const elementBDecision = decisions.find(d => d.element.id === conflict.elementB);
        
        if (elementADecision && elementBDecision) {
          // Prioritize based on content priority
          const lowerPriorityDecision = 
            elementADecision.element.content.priority === 'optional' ? elementADecision :
            elementBDecision.element.content.priority === 'optional' ? elementBDecision :
            elementADecision.element.content.priority === 'important' ? elementADecision :
            elementBDecision;

          // Change to non-conflicting mode
          if (conflict.resolution === 'external-label') {
            lowerPriorityDecision.chosenMode = 'external-label';
          } else if (conflict.resolution === 'hide') {
            lowerPriorityDecision.chosenMode = 'hidden';
          }
        }
      }
    });

    return decisions;
  }

  private static generatePlanRecommendations(
    decisions: ElementDecision[],
    analysis: any,
    config: SmartLayoutConfig
  ): string[] {
    const recommendations: string[] = [];
    
    const fallbackCount = decisions.filter(d => d.fallbackUsed).length;
    const hiddenCount = decisions.filter(d => d.chosenMode === 'hidden').length;
    const externalLabelCount = decisions.filter(d => d.chosenMode === 'external-label').length;
    
    if (fallbackCount > decisions.length / 2) {
      recommendations.push('Consider increasing container size - many elements using fallback rendering');
    }
    
    if (hiddenCount > 0) {
      recommendations.push(`${hiddenCount} elements will be hidden - consider external labels or larger container`);
    }
    
    if (externalLabelCount > 3) {
      recommendations.push('Many external labels may create visual clutter - consider conceptual representation');
    }
    
    if (analysis.overallAssessment.qualityScore < 0.6) {
      recommendations.push('Overall layout quality is low - consider redesigning for better readability');
    }

    return recommendations;
  }

  private static generateAbbreviatedText(text: string): string {
    if (!text || text.length <= 20) return text;
    
    const words = text.split(' ');
    if (words.length === 1) {
      return text.length > 10 ? text.substring(0, 8) + '...' : text;
    }
    
    // Try to keep first half of words
    const halfWords = Math.ceil(words.length / 2);
    const abbreviated = words.slice(0, halfWords).join(' ');
    
    return abbreviated.length > 30 ? abbreviated.substring(0, 27) + '...' : abbreviated + '...';
  }

  private static getDefaultIcon(contextType: string): string {
    switch (contextType) {
      case 'button': return '▶';
      case 'header': return '■';
      case 'label': return '●';
      default: return '◆';
    }
  }

  private static calculateExternalLabelPosition(
    element: SmartElement,
    availableSpace: Rectangle
  ): Rectangle {
    // Place label to the right of the element by default
    return {
      x: availableSpace.x + availableSpace.width + 10,
      y: availableSpace.y,
      width: 100, // Default label width
      height: availableSpace.height
    };
  }
}

/**
 * Text Analysis Utilities (Phase 2)
 * These will be implemented in Phase 2 for text measurement and fitting
 */
export class TextAnalyzer {
  /**
   * Estimate how many lines text will take with given constraints
   */
  static estimateTextLines(
    text: string,
    maxWidth: number,
    fontSize: number,
    fontFamily: string = 'Space Grotesk'
  ): number {
    // Phase 2: Implementation
    // Handle empty text
    if (!text || text.length === 0) {
      return 1; // Empty text still takes 1 line
    }
    
    // For now, return a basic estimate
    const avgCharWidth = fontSize * 0.55;
    const charsPerLine = Math.floor(maxWidth / avgCharWidth);
    const effectiveCharsPerLine = Math.floor(charsPerLine * 0.9); // Word wrapping buffer
    return Math.ceil(text.length / effectiveCharsPerLine);
  }
  
  /**
   * Calculate minimum font size needed for text to fit in container
   */
  static calculateMinFontSize(
    text: string,
    container: Rectangle,
    maxLines: number = 3
  ): number {
    // Phase 2: Implementation
    // For now, return a basic calculation
    const avgCharWidth = 0.55;
    const charsPerLine = container.width / avgCharWidth;
    const totalChars = text.length;
    const requiredLines = Math.ceil(totalChars / charsPerLine);
    const availableHeight = container.height / Math.min(requiredLines, maxLines);
    
    return Math.max(8, Math.min(availableHeight * 0.8, 16));
  }
  
  /**
   * Test if text fits in container with given constraints
   */
  static testTextFit(
    text: string,
    container: Rectangle,
    fontSize: number,
    maxLines: number = 3
  ): RenderQuality {
    // Phase 2: Implementation
    const lines = this.estimateTextLines(text, container.width, fontSize);
    const requiredHeight = lines * fontSize * 1.2; // Line height
    const fits = requiredHeight <= container.height && lines <= maxLines;
    
    return {
      fits,
      readabilityScore: fits ? Math.min(fontSize / 12, 1) : 0,
      aestheticScore: fits ? 0.8 : 0.2,
      informationRetention: fits ? 1 : 0.5,
      overallScore: fits ? 0.8 : 0.3
    };
  }
}

/**
 * Development and debugging utilities
 */
export class SmartLayoutDebug {
  /**
   * Log smart layout decisions for debugging
   */
  static logDecisions(plan: RenderPlan, config: SmartLayoutConfig): void {
    if (!config.debug) return;
    
    console.group('Smart Layout Decisions');
    console.log('Overall Quality:', plan.overallQuality);
    console.log('Elements:', plan.elements.length);
    console.log('Has External Labels:', plan.hasExternalLabels);
    
    plan.elements.forEach((decision, index) => {
      console.log(`Element ${index}: ${decision.element.id} -> ${decision.chosenMode} (quality: ${decision.quality.overallScore})`);
    });
    
    if (plan.recommendations.length > 0) {
      console.log('Recommendations:', plan.recommendations);
    }
    
    console.groupEnd();
  }
  
  /**
   * Validate smart layout configuration in development
   */
  static validateInDevelopment(config: SmartLayoutConfig): void {
    if (process.env.NODE_ENV !== 'development') return;
    
    const validation = SmartLayoutEngine.validateConfig(config);
    
    if (validation.errors.length > 0) {
      console.error('Smart Layout Configuration Errors:', validation.errors);
    }
    
    if (validation.warnings.length > 0) {
      console.warn('Smart Layout Configuration Warnings:', validation.warnings);
    }
  }
}